import React from "react";
import { getWebsiteDocumentUrl, termsAndConditionsLink } from "../../../utils/content";
import Show from "../../../utils/show.component";
import { usd } from "../../../utils/utils";

export const determineCancelDisclosure = (
  frequency,
  day,
  lastFour,
  amount,
  openEndType,
  isOpenEnded,
  isInternalTransfer
) => {
  const internalOrElectronic = isInternalTransfer ? "internal" : "electronic";
  
  if (isOpenEnded) {
    return openEndMonthlyCancellation(day, lastFour, openEndType, internalOrElectronic);
  }

  if (frequency === "Monthly") {
    return closedEndMonthlyCancellation(frequency, day, lastFour, amount, internalOrElectronic);
  }

  if (frequency === "Weekly") {
    return weeklyCancellation(frequency, day, lastFour, amount, internalOrElectronic);
  }

  if (frequency === "Bi-Weekly") {
    return biweeklyCancellation(frequency, day, lastFour, amount, internalOrElectronic);
  }
};

const openEndMonthlyCancellation = (dayWithOrdinal, lastFour, openEndType, internalOrElectronic) =>
  `By clicking Continue, I authorize Lake Trust to cancel the recurring ${internalOrElectronic} funds transfer on the ${dayWithOrdinal} of each month from my account ending in ${lastFour} for the ${openEndType}. I understand that my payment will not be made automatically and I will be responsible for making my loan payment separately.`;

const closedEndMonthlyCancellation = (frequency, dayWithOrdinal, lastFour, amount, internalOrElectronic) =>
  `By clicking Continue, I authorize Lake Trust to cancel the ${frequency} recurring ${internalOrElectronic} funds transfers on the ${dayWithOrdinal} of each month from my account ending in ${lastFour} for the amount of ${amount}. I understand that my payment will not be made automatically and I will be responsible for making my loan payment separately.`;

const weeklyCancellation = (frequency, dayOfWeek, lastFour, amount, internalOrElectronic) =>
  `By clicking Continue, I authorize Lake Trust to cancel the  ${frequency} recurring ${internalOrElectronic} funds transfers on ${dayOfWeek} of each week from my account ending in ${lastFour} for the amount of ${amount}. I understand that my payment will not be made automatically and I will be responsible for making my loan payment separately.`;

const biweeklyCancellation = (frequency, dayOfWeek, lastFour, amount, internalOrElectronic) =>
  `By clicking Continue, I authorize Lake Trust to cancel the  ${frequency} recurring ${internalOrElectronic} funds transfers on every other ${dayOfWeek} from my account ending in ${lastFour} for the amount of ${amount}. I understand that my payment will not be made automatically and I will be responsible for making my loan payment separately.`;

export const StandardAchDisclosure = (showPrivacyPolicy = false) => {
  return (
    <div className="f12 italic">
      <div className="mb-2">
        <span className="bold">Payment Authorization:</span> By clicking “SUBMIT PAYMENT”, I
        authorize Lake Trust Credit Union to conduct the electronic funds transfer in the amount
        indicated above from the designated account I requested. If the date of the transfer falls
        on a weekend or Federal Reserve Bank holiday, the transfer will be made the following
        business day.
      </div>
      <div className="mb-2">
        <span className="bold">Insufficient Funds:</span> By authorizing this transaction, I
        acknowledge and agree that if I do not have sufficient funds in my designated account, the
        requested payment will be reversed from my Lake Trust loan, and I will be responsible for
        making my loan payment separately. I also understand that I am solely responsible for any
        resulting late fees or other charges incurred on my loan and/or charges my other financial
        institution may impose as a result of the reversed payment.
      </div>
      <div>
        I also confirm that the information I provided above is accurate and complete, I am 18 years
        or older, I am legally authorized to transact on the account identified, and I understand
        and agree that this payment request is governed by{" "}
        <a href={termsAndConditionsLink()} target="_blank" style={{ fontSize: "inherit" }}>
          Lake Trust's Account Terms and Conditions
        </a>
        <Show condition={showPrivacyPolicy === true}>
          <span> and </span>
          <a
            href={`${getWebsiteDocumentUrl()}/LakeTrustWebsite/media/Assets/Privacy-Policy-Disclosure.pdf`}
            target="_blank"
            style={{ fontSize: "inherit" }}
          >
            Lake Trust's Privacy Policy
          </a>
        </Show>
        .
      </div>
    </div>
  );
};

export const StandardDebitDisclosure = (showPrivacyPolicy = false) => {
  return (
    <div className="f12 italic">
      <div className="mb-2">
        <span className="bold">Payment Authorization:</span> By clicking “SUBMIT PAYMENT”, I
        authorize Lake Trust Credit Union to initiate, and my financial institution to honor, a
        ONE-TIME electronic payment in the amount specified above from the bank account associated
        with my debit card noted above. I understand and agree that this method of payment (debit
        card) has a $4.95 convenience fee charged for EACH one-time payment made with my debit card,
        and I am aware that there are alternative fee-free payment methods available to me –
        including paying with cash, check, or ACH transfer.
      </div>
      <div className="mb-2">
        <span className="bold">Insufficient Funds:</span> By authorizing this transaction, I
        acknowledge and agree that if I do not have sufficient funds in my designated account, the
        requested payment will be reversed from my Lake Trust loan, and I will be responsible for
        making my loan payment separately. I also understand that I am solely responsible for any
        resulting late fees or other charges incurred on my loan and/or charges my other financial
        institution may impose as a result of the reversed payment.
      </div>
      <div>
        I also confirm that the information I provided above is accurate and complete, I am 18 years
        or older, I am legally authorized to transact on the account identified, and I understand
        and agree that this payment request is governed by{" "}
        <a href={termsAndConditionsLink()} target="_blank" style={{ fontSize: "inherit" }}>
          Lake Trust's Account Terms and Conditions
        </a>
        <Show condition={showPrivacyPolicy === true}>
          <span> and </span>
          <a
            href={`${getWebsiteDocumentUrl()}/LakeTrustWebsite/media/Assets/Privacy-Policy-Disclosure.pdf`}
            target="_blank"
            style={{ fontSize: "inherit" }}
          >
            Lake Trust's Privacy Policy
          </a>
        </Show>
        .
      </div>
    </div>
  );
};

export const StandardSkipDisclosure = (
  skippedDates,
  nextDueDate,
  accountLastFour,
  totalFeeAmount,
  paymentMethod,
  hasNegativeBalance
) => {
  const conditionalPaymentWording =
    paymentMethod === "Debit Card"
      ? `charge my debit card ending in ${accountLastFour}`
      : "debit my Lake Trust account";

  const negativeBalanceWording =
    hasNegativeBalance && paymentMethod === "Debit Card"
      ? ` and amount to bring my Lake Trust account in good standing`
      : "";

  const numberOfSkips = skippedDates.length > 1 ? "two" : "one";

  const skippedDateWording =
    skippedDates.length > 1 ? `${skippedDates[0]} and ${skippedDates[1]}` : skippedDates[0];

  return (
    <div className="f12 italic">
      <p>
        I authorize Lake Trust to skip my {skippedDateWording} payment(s) and advance my loan due
        date {numberOfSkips} month(s).
      </p>
      <p>By authorizing a Skip-A-Pay request, I also understand that:</p>{" "}
      <p>My monthly payment will resume after the skipped month on {nextDueDate}.</p>
      <p>
        {" "}
        All other terms and conditions of my original Loan Agreement will remain the same and I am
        still responsible for the entire outstanding balance of principal and interest until the
        loan is fully repaid.
      </p>
      <p>
        My loan maturity date may be extended, and interest will accrue on the skipped payment and
        entire loan balance during the month of my skipped payment.
      </p>
      <p>
        Any credit insurance, debt protection, or GAP coverage on my loan may not extend beyond the
        original maturity date and may reduce the GAP claim amount.
      </p>
      <p>
        I am responsible for cancelling my external loan payment for the month of my skipped
        payment.
      </p>
      <p>
        A $35.00 Skip-a-Pay fee will be assessed for each approved Skip-a-Pay request. The $35.00
        fee will not be applied to the principal or as a loan payment.
      </p>
      <p>
        I authorize Lake Trust Credit Union to {conditionalPaymentWording} for the{" "}
        {usd.format(totalFeeAmount)} Skip-A-Pay fee(s){negativeBalanceWording}. I also understand
        and agree to the Skip-A-Pay terms and conditions stated above and authorize Lake Trust to
        submit my Skip-A-Pay request.
      </p>
    </div>
  );
};

export const OpenEndRecurringDisclosure = (isInternalTransfer) => {
  const internalOrElectronic = isInternalTransfer ? "internal" : "electronic";

  const nsfText = isInternalTransfer ? 
  `By authorizing this transaction, I acknowledge and agree that if I do not have sufficient funds in my account, the requested 
  payment will NOT be made to my Lake Trust loan and I will be responsible for any late fees and for making my loan payment separately.`
  :
  `By authorizing this transaction, I acknowledge and agree that if I do not have sufficient funds in my designated account, the
  requested payment will be reversed from my Lake Trust loan, and I will be responsible for making my loan payment separately.
  I also understand that I am solely responsible for any resulting late fees or other charges incurred on my loan and/or charges
  my other financial institution may impose as a result of the reversed payment.`;

  return (
    <div className="f12 italic">
      <div className="mb-2">
        <span className="bold">AutoPay Setup:</span> By clicking “SUBMIT PAYMENT”, I authorize Lake
        Trust Credit Union to set up recurring {internalOrElectronic} funds transfers (“AutoPay”) each month in
        the amount indicated above (either Minimum Amount Due OR Total Balance) on the date selected
        from the designated account.
        <Show condition={!isInternalTransfer}>
          {" "}If the date of the transfer falls on a weekend or Federal
          Reserve Bank holiday, the transfer will be made the following business day.
        </Show>
      </div>
      <div className="mb-2">
        <span className="bold">Insufficient Funds:</span> {nsfText}
      </div>
      <div className="mb-2">
        <span className="bold">How to Cancel:</span> I understand that this recurring {internalOrElectronic}{" "}
        payment request will remain in full force and effect until I provide notice to Lake Trust
        that I wish to cancel all recurring {internalOrElectronic} funds transfers by contacting Lake Trust
        prior to the next scheduled transfer. Cancellations may be made by calling Lake Trust at
        888.267.7200, using the “Cancel AutoPay” feature in my Lake Trust online banking/mobile
        account, or visiting a branch.
      </div>
      <div>
        I also confirm that the information I provided above is accurate and complete, I am 18 years
        or older, I am legally authorized to transact on the account identified, and I understand
        and agree that this payment request is governed by{" "}
        <a className="f12" href={termsAndConditionsLink()} target="_blank">
          Lake Trust's Account Terms and Conditions.
        </a>
      </div>
    </div>
  );
};

export const ClosedEndRecurringDisclosure = (isInternalTransfer) => {
  const internalOrElectronic = isInternalTransfer ? "internal" : "electronic";

  const nsfText = isInternalTransfer ? 
  `By authorizing this transaction, I acknowledge and agree that if I do not have sufficient funds in my account, the requested 
  payment will NOT be made to my Lake Trust loan and I will be responsible for any late fees and for making my loan payment separately.`
  :
  `By authorizing this transaction, I acknowledge and agree that if I do not have sufficient funds in my designated account, the
  requested payment will be reversed from my Lake Trust loan, and I will be responsible for making my loan payment separately.
  I also understand that I am solely responsible for any resulting late fees or other charges incurred on my loan and/or charges
  my other financial institution may impose as a result of the reversed payment.`;

  return (
    <div className="f12 italic">
      <div className="mb-2">
        <span className="bold">AutoPay Setup:</span> By clicking “SUBMIT PAYMENT”, I authorize Lake
        Trust Credit Union to set up recurring {internalOrElectronic} funds transfers (“AutoPay”) in the amount
        indicated above from the designated account at the frequency (monthly/weekly/bi-weekly) I
        requested.
        <Show condition={!isInternalTransfer}>
          {" "}If the date of the transfer falls on a weekend or Federal
          Reserve Bank holiday, the transfer will be made the following business day.
        </Show>
      </div>
      <div className="mb-2">
        <span className="bold">Insufficient Funds:</span> {nsfText}
      </div>
      <div className="mb-2">
        <span className="bold">How to Cancel:</span> I understand that this recurring {internalOrElectronic}{" "}
        payment request will remain in full force and effect until I provide notice to Lake Trust
        that I wish to cancel all recurring {internalOrElectronic} funds transfers by contacting Lake Trust
        prior to the next scheduled transfer. Cancellations may be made by calling Lake Trust at
        888.267.7200, using the “Cancel AutoPay” feature in my Lake Trust online banking/mobile
        account, or visiting a branch.
      </div>
      <div>
        I also confirm that the information I provided above is accurate and complete, I am 18 years
        or older, I am legally authorized to transact on the account identified, and I understand
        and agree that this payment request is governed by{" "}
        <a className="f12" href={termsAndConditionsLink()} target="_blank">
          Lake Trust's Account Terms and Conditions
        </a>
      </div>
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/components/authenticated-payments/misc/disclosures.js